/**
 * Les chiffres d'un lycéen sur lui-même — les phrases, sans écran.
 *
 * Le 29 septembre 2026, Hedi a assoupli l'invariant §14.1 **pour le lycée
 * seulement** : un élève de terminale qui pilote son année lit son score, ses
 * matières et sa moyenne comme un outil. Le collège garde l'invariant entier —
 * le serveur ne lui envoie pas ces chiffres (`core_api/internal/niveau.py`) et
 * la coquille ne les dessine pas (`profile.ownNumbers`).
 *
 * Les phrases sont écrites ici et non importées de `parent/` : la coquille
 * enfant n'a pas le droit d'importer la coquille parent. Elles disent la même
 * chose que la jauge parent — surtout la pondération : une moyenne à poids égal
 * **se dit**, sinon elle se lit comme celle du bulletin.
 */

import type { ActiviteDeMatiere, MesChiffres, PositionGlobale } from "@/api/schema";

/** « 62 sur 100 » : le score de `mastery`, arrondi — 50 est l'attendu. */
export function scoreSur100(score: number): string {
  return `${Math.round(score)} sur 100`;
}

/** Ce que 50 veut dire, écrit une fois pour tout l'écran. */
export const REPERE_DU_SCORE =
  "50, c'est le niveau attendu à cette date de l'année — pas la moyenne sur 20.";

/** La ligne sous la moyenne : combien de matières, et avec quels poids. */
export function ligneDeLaMoyenne(ensemble: PositionGlobale): string {
  const couverture = `${ensemble.subjects_measured} matière${
    ensemble.subjects_measured > 1 ? "s" : ""
  } mesurée${ensemble.subjects_measured > 1 ? "s" : ""} sur ${ensemble.subjects_total}`;
  if (ensemble.weighting === "uniforme") {
    return `${couverture}, à poids égal — aucun coefficient importé.`;
  }
  return `${couverture}, pondérées par les coefficients de ton établissement.`;
}

/** Le score d'une compétence mesurée, par identifiant ; absente = non mesurée. */
export function scoresParCompetence(chiffres: MesChiffres | null | undefined): Map<string, number> {
  return new Map((chiffres?.competences ?? []).map((c) => [c.competency_id, c.score]));
}

/** « 4 exercices faits, 3 réussis du premier coup ». */
export function ligneDActivite(ligne: ActiviteDeMatiere): string {
  const faits = `${ligne.faits} exercice${ligne.faits > 1 ? "s" : ""} fait${ligne.faits > 1 ? "s" : ""}`;
  const reussis = `${ligne.reussis} réussi${ligne.reussis > 1 ? "s" : ""} du premier coup`;
  return `${faits}, ${reussis}`;
}
